import { useState } from "react";

import { useParams } from "react-router-dom";

import useSWR from "swr";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { Tab, Tabs } from "@mui/material";

import { useRefreshToken } from "../../common/hooks/useRefreshToken";
import ViewPage from "../../layouts/ViewPage";
import LabelAndDisplay from "../../components/LabelAndDisplay";
import ImageContainer from "../../components/shared/ImageContainer";

import Wysiwyg from "../../components/shared/Wysiwyg";

import { getTranslation, languages } from "../../utils/translate";
import PropTypes from "prop-types";
import RecipeGrid from "../../components/RecipeGrid";
import BeefCutsGrid from "../../components/BeefCutsGrid";
import { getAuthorDisplayName } from "../../utils/author";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

function AuthorView() {
  useRefreshToken();

  const params = useParams();

  const [language, setLanguage] = useState(languages[0]);
  const [value, setValue] = useState(0);
  let title = "";
  let description = "";

  let fields = [];
  let sidebarFields = [];

  const payload = {
    fields: ["*", "translations.*"],
  };

  let { data: author } = useSWR({
    url: `recipe_author/${params.id}`,
    ...payload,
  });

  if (author) {
    const translatedItem = getTranslation(author, {
      key: "languages_code",
      code: language.code,
    });

    const image = author?.image
      ? `${process.env.REACT_APP_API_URL}/assets/${author.image}`
      : null;

    title = getAuthorDisplayName(author);

    fields = [
      {
        key: "Display Name",
        value: author.display_name,
      },
      {
        key: "First Name",
        value: author.first_name,
      },
      {
        key: "Last Name",
        value: author.last_name,
      },
      {
        key: "Biography",
        children: (
          <Typography>
            <Wysiwyg>{translatedItem.biography}</Wysiwyg>
          </Typography>
        ),
      },
    ];

    sidebarFields = [
      {
        key: "Image",
        children: (
          <ImageContainer
            url={image}
            alt={translatedItem.title}
            sx={{
              height: "20rem",
            }}
          />
        ),
      },
    ];
  }

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const recipesPayload = {
    url: `recipe?meta=*`,
    fields: ["*", "translations.*"],
  };

  const beefCutsPayload = {
    url: `beef_product?meta=*`,
    fields: ["*", "translations.*"],
  };

  return (
    <>
      <ViewPage
        title={title}
        description={description}
        breadcrumbsLinks={[
          {
            url: "/collections-menu",
            content: "Collections & Cuisine",
          },
          {
            url: `/authors`,
            content: "Authors",
          },
        ]}
        language={language}
        setLanguage={setLanguage}
      >
        <Box sx={{ flexGrow: 1, my: 3 }}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              width: "100%",
              display: {
                xs: "none",
                lg: "block",
              },
            }}
          >
            <Tabs value={value} onChange={handleChange}>
              <Tab label="Overview" {...a11yProps(0)} />
              <Tab label="Recipes" {...a11yProps(1)} />
              <Tab label="Beef Cuts" {...a11yProps(2)} />
            </Tabs>
          </Box>

          <CustomTabPanel value={value} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={7} lg={8}>
                <Box sx={{ flexGrow: 1, my: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      {fields.map((field) => (
                        <LabelAndDisplay
                          key={field.key}
                          label={field.key}
                          value={field.value}
                        >
                          {field.children}
                        </LabelAndDisplay>
                      ))}
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12} md={5} lg={4}>
                <Box sx={{ flexGrow: 1, my: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      {sidebarFields.map((field) => (
                        <LabelAndDisplay
                          direction="column"
                          key={field.key}
                          label={field.key}
                          value={field.value}
                        >
                          {field.children}
                        </LabelAndDisplay>
                      ))}
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </CustomTabPanel>

          <CustomTabPanel value={value} index={1}>
            <Typography className="form-section-title" variant="h4">
              Recipes
            </Typography>

            <Box sx={{ flexGrow: 1, my: 3 }}>
              <RecipeGrid payload={recipesPayload} language={language} />
            </Box>
          </CustomTabPanel>

          <CustomTabPanel value={value} index={2}>
            <Typography className="form-section-title" variant="h4">
              Beef Cuts
            </Typography>

            <Box sx={{ flexGrow: 1, my: 3 }}>
              <BeefCutsGrid payload={beefCutsPayload} language={language} />
            </Box>
          </CustomTabPanel>
        </Box>
      </ViewPage>
    </>
  );
}

export default AuthorView;
